import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { tvShows, categories } from '@/mocks/tv-shows';
import { PageMeta, JsonLd } from '@/components/feature/SeoComponents';
import TvShowCard from '@/pages/home/components/TvShowCard';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'newest' | 'popular'>('popular');
  const [activeTag, setActiveTag] = useState<string>('all');

  const category = categories.find(c => c.slug === slug) || categories[0];

  const filteredShows = useMemo(() => {
    let shows = slug === 'all'
      ? tvShows
      : tvShows.filter(s => s.categorySlug === slug);

    if (activeTag !== 'all') {
      shows = shows.filter(s => s.tags.includes(activeTag));
    }

    if (sortBy === 'rating') {
      return [...shows].sort((a, b) => b.rating - a.rating);
    }
    if (sortBy === 'newest') {
      return [...shows].sort((a, b) => b.year - a.year);
    }
    return shows;
  }, [slug, activeTag, sortBy]);

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    const shows = slug === 'all' ? tvShows : tvShows.filter(s => s.categorySlug === slug);
    shows.forEach(s => s.tags.forEach(t => tagSet.add(t)));
    return ['all', ...Array.from(tagSet)];
  }, [slug]);

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 老子影视网`,
    description: `老子影视网${category.name}大全，全集免费在线观看，高清画质不缓冲。`,
    url: `https://www.laoziys.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '老子影视网',
      url: 'https://www.laoziys.com',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://www.laoziys.com',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: category.name,
          item: `https://www.laoziys.com/category/${slug}`,
        },
      ],
    },
  };

  return (
    <>
      <PageMeta
        title={`${category.name} | 电视剧大全 全集播放 | 老子影视网`}
        description={`老子影视网${category.name}大全，全集免费在线观看，高清画质不缓冲，追剧必备。涵盖最新${category.name}资源。`}
        keywords={`${category.name},电视剧大全,全集播放,高清,免费观看,老子影视网`}
        canonicalUrl={`https://www.laoziys.com/category/${slug}`}
      />
      <JsonLd data={categoryJsonLd} />

      <main className="pt-20">
        <nav className="w-full px-4 md:px-6 py-4" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm text-foreground-500">
            <li><Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li className="text-foreground-900 font-medium">{category.name}</li>
          </ol>
        </nav>

        <section className="w-full px-4 md:px-6 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                <i className={`${category.icon} text-primary-500 mr-2`}></i>
                {category.name}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共 {filteredShows.length} 部{category.name}资源
              </p>
            </div>

            <div className="flex items-center gap-2 bg-background-100 rounded-full p-1">
              {[
                { key: 'popular' as const, label: '热门', icon: 'ri-fire-line' },
                { key: 'rating' as const, label: '评分', icon: 'ri-star-line' },
                { key: 'newest' as const, label: '最新', icon: 'ri-time-line' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${opt.icon} text-sm`}></i>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-8">
            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() => setActiveTag(tag)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTag === tag
                    ? 'bg-accent-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                }`}
              >
                {tag === 'all' ? '全部标签' : tag}
              </button>
            ))}
          </div>

          {filteredShows.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {filteredShows.map((show) => (
                <TvShowCard key={show.id} show={show} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">暂无符合条件的电视剧</p>
            </div>
          )}
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
            其他分类推荐
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {categories.filter(c => c.slug !== 'all' && c.slug !== slug).slice(0, 5).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-2 p-3 rounded-lg bg-background-100 hover:bg-background-200 border border-background-200/70 transition-all duration-200 cursor-pointer"
              >
                <i className={`${cat.icon} text-primary-500 text-sm`}></i>
                <span className="text-sm font-medium text-foreground-700">{cat.name}</span>
              </Link>
            ))}
          </div>
        </section>
      </main>
    </>
  );
}